import { X } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"

import { BarraLateral } from "./BarraLateral"

/**
 * El menú en el teléfono: la misma barra lateral, en un cajón que entra por la
 * izquierda. Lo abre el botón de menú del encabezado; por encima de 1024 px no se
 * monta (ver `lib/pantalla.ts`).
 *
 * ⚠ **Va en un portal a `body`, como `Cajon`.** El encabezado y el área de trabajo
 * llevan `backdrop-filter`, y eso atrapa a cualquier descendiente `fixed`: dentro
 * de ellos, el cajón mediría lo que mide el encabezado y no la pantalla.
 *
 * ⚠ **Y por salir del armazón se lleva `.apariencia-clara` consigo.** Esa clase
 * vive en `AppShell` y es la que redefine la tinta del marco; en el portal no la
 * hereda, y en el modo claro el menú saldría oscuro sobre una pantalla blanca.
 *
 * ⚠ **Es modal: Escape lo cierra, el foco entra y vuelve, y el fondo no se mueve.**
 * Lo mismo que `Modal`, y por lo mismo: lo tapado sigue siendo pulsable.
 */
export function MenuMovil({
  abierto,
  claro,
  onCerrar,
}: {
  abierto: boolean
  claro: boolean
  onCerrar: () => void
}) {
  const sinMovimiento = useReducedMotion()
  const cerrar = useRef<HTMLButtonElement | null>(null)
  const alCerrar = useRef(onCerrar)
  useEffect(() => {
    alCerrar.current = onCerrar
  })

  useEffect(() => {
    if (!abierto) return
    const origen = document.activeElement as HTMLElement | null
    cerrar.current?.focus()
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation()
        alCerrar.current()
      }
    }
    document.addEventListener("keydown", alTeclear, true)
    const desbordeAnterior = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", alTeclear, true)
      document.body.style.overflow = desbordeAnterior
      origen?.focus()
    }
  }, [abierto])

  return createPortal(
    <AnimatePresence>
      {abierto && (
        <div className={`no-print fixed inset-0 z-50 ${claro ? "apariencia-clara" : ""}`}>
          <motion.div
            aria-hidden={true}
            className="absolute inset-0"
            style={{ backgroundColor: "rgb(2 6 23 / 0.6)", backdropFilter: "blur(3px)" }}
            initial={sinMovimiento ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: sinMovimiento ? 0 : 0.16 }}
            onClick={onCerrar}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Menú"
            className="caja-menu-movil absolute top-0 bottom-0 left-0 flex w-72 max-w-[calc(100vw-3rem)] flex-col"
            initial={sinMovimiento ? false : { x: "-100%" }}
            animate={{ x: 0 }}
            exit={sinMovimiento ? { opacity: 0 } : { x: "-100%" }}
            transition={
              sinMovimiento
                ? { duration: 0 }
                : { type: "spring", stiffness: 420, damping: 40, mass: 0.8 }
            }
          >
            {/* El botón de cerrar flota sobre la esquina del panel, fuera del área
                que se desplaza: con el menú largo tiene que seguir a mano. 44 px,
                que es lo que pide algo que se pulsa a diario con el pulgar. */}
            <button
              ref={cerrar}
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar el menú"
              className="foco-cristal absolute top-2 right-2 z-10 flex h-11 w-11 items-center justify-center rounded-full"
              style={{ color: "rgb(var(--marco-tinta) / 0.85)" }}
            >
              <X size={20} strokeWidth={1.5} aria-hidden={true} />
            </button>
            <BarraLateral enCajon onNavegar={onCerrar} />
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
